import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';

export default function ScannedCertificateListItem({
  scan,
  index,
  alternateBackground,
  onDelete,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleView = () => {
    navigate(`/certificates/scanned/${scan.id}`);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async (scanToDelete) => {
    setIsDeleting(true);
    try {
      await onDelete(scanToDelete);
      setDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'error':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'processing':
        return <ScheduleIcon color="warning" fontSize="small" />;
      default:
        return <ScheduleIcon color="disabled" fontSize="small" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready':
        return 'success';
      case 'error':
        return 'error';
      case 'processing':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatConfidence = (confidence) => {
    if (typeof confidence !== 'number') return '-';
    return `${Math.round(confidence * 100)}%`;
  };

  const getCertificateTypeLabel = (type) => {
    switch (type) {
      case 'gauged_container':
        return t('scanned_certificates.gauged_container');
      case 'tanker_truck':
        return t('scanned_certificates.tanker_truck');
      default:
        return type || '-';
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          backgroundColor: alternateBackground ? 'action.hover' : 'background.paper',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            backgroundColor: 'action.selected',
          },
          cursor: 'pointer',
        }}
        onClick={handleView}
      >
        <Typography
          variant="body2"
          sx={{
            flex: '0 0 25%',
            fontWeight: scan.status === 'ready' ? 'medium' : 'normal',
          }}
        >
          {scan.originalFileName || '-'}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            flex: '0 0 12%',
            color: 'text.secondary',
          }}
        >
          {getCertificateTypeLabel(scan.certificateType)}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            flex: '0 0 15%',
            color: 'text.secondary',
          }}
        >
          {scan.parser?.type || '-'}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            flex: '0 0 10%',
            color: 'text.secondary',
          }}
        >
          {formatConfidence(scan.parser?.confidence)}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            flex: '0 0 18%',
            color: 'text.secondary',
          }}
        >
          {formatDate(scan.parser?.processedAt)}
        </Typography>

        <Box
          sx={{
            flex: '0 0 15%',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {getStatusIcon(scan.status)}
          <Chip
            label={t(`scanned_certificates.status.${scan.status}`)}
            color={getStatusColor(scan.status)}
            size="small"
            variant="outlined"
          />
        </Box>

        <Box
          sx={{
            flex: '0 0 10%',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 0.5,
          }}
        >
          <Tooltip title={t('scanned_certificates.view')}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleView();
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('scanned_certificates.delete')}>
            <IconButton
              size="small"
              onClick={handleDeleteClick}
              disabled={isDeleting}
              sx={{
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.light',
                  color: 'error.contrastText',
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        scan={scan}
        isDeleting={isDeleting}
      />
    </>
  );
}
